import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from 'react-i18next';

import '../styles/onboarding.css';

import logo from '../assets/logo.png';
import LanguageSelector from './LanguageSelector';

import onboarding01 from '../assets/onboarding/onboarding-01.webp';
import onboarding02 from '../assets/onboarding/onboarding-02.webp';
import onboarding03 from '../assets/onboarding/onboarding-03.webp';
import onboarding04 from '../assets/onboarding/onboarding-04.webp';
import onboarding05 from '../assets/onboarding/onboarding-05.webp';
import onboarding06 from '../assets/onboarding/onboarding-06.webp';

const slides = [
  {
    id: 1,
    title: 'onboarding.titre1',
    text: 'onboarding.text1',
    image: onboarding01,
    alt: 'Espace de communication réunissant messages, fichiers, appels vidéo et agenda',
  },
  {
    id: 2,
    title: 'onboarding.titre2',
    text: 'onboarding.text2',
    image: onboarding02,
    alt: 'Conversation avec partage de fichiers',
  },
  {
    id: 3,
    title: 'onboarding.titre3',
    text: 'onboarding.text3',
    image: onboarding03,
    alt: 'Appel vidéo avec plusieurs participants',
  },
  {
    id: 4,
    title: 'onboarding.titre4',
    text: 'onboarding.text4',
    image: onboarding04,
    alt: 'Agenda partagé et bloc-notes collaboratif',
  },
  {
    id: 5,
    title: 'onboarding.alertesTitre',
    text: 'onboarding.alertesText',
    fallbackTitle: 'Alertes et suivi, restez informé.',
    fallbackText:
      'Ne manquez rien d’important. Suivez vos activités et celles de vos équipes en temps réel.',
    image: onboarding05,
    alt: 'Alertes de mentions, échéances, fichiers et suivi d’activité',
  },
  {
    id: 6,
    title: 'onboarding.titre6',
    text: 'onboarding.text6',
    image: onboarding06,
    alt: 'Espace sécurisé avec chiffrement de bout en bout',
  },
];

const Onboarding = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState('next');

  const navigate = useNavigate();
  const { t } = useTranslation();

  const currentSlide = slides[currentIndex];
  const isLastSlide = currentIndex === slides.length - 1;

  const translate = (key, fallback) =>
    t(key, {
      defaultValue: fallback,
    });

  const goToLogin = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    navigate('/connexion');
  };

  const handleNext = () => {
    if (!isLastSlide) {
      setDirection('next');
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    goToLogin();
  };

  const handlePrev = () => {
    if (currentIndex === 0) return;

    setDirection('prev');
    setCurrentIndex((prev) => prev - 1);
  };

  const goToSlide = (index) => {
    if (index === currentIndex) return;

    setDirection(index > currentIndex ? 'next' : 'prev');
    setCurrentIndex(index);
  };

  const handlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    trackMouse: true,
    preventScrollOnSwipe: true,
    delta: 45,
  });

  const title = currentSlide.fallbackTitle
    ? translate(currentSlide.title, currentSlide.fallbackTitle)
    : t(currentSlide.title);

  const text = currentSlide.fallbackText
    ? translate(currentSlide.text, currentSlide.fallbackText)
    : t(currentSlide.text);

  return (
    <main className="onboarding" {...handlers}>
      {/* Décoration d'arrière-plan */}
      <div
        className="onboarding-orb onboarding-orb-1"
        aria-hidden="true"
      />

      <div
        className="onboarding-orb onboarding-orb-2"
        aria-hidden="true"
      />

      {/* HEADER */}
      <header className="onboarding-header">
        <div className="onboarding-brand">
          <img
            src={logo}
            alt={t('menu.titre')}
          />

          <span>{t('menu.titre')}</span>
        </div>

        <div className="language-pill">
          <LanguageSelector />
        </div>
      </header>

      {/* CONTENU PRINCIPAL */}
      <section
        className="onboarding-main"
        aria-live="polite"
      >
        {/* VISUEL */}
        <div
          key={`visual-${currentIndex}`}
          className={`onboarding-visual animation-${direction}`}
        >
          <div className="onboarding-visual-frame">
            <img
              src={currentSlide.image}
              alt={currentSlide.alt}
              className="onboarding-visual-image"
              draggable="false"
            />
          </div>
        </div>

        {/* TEXTE */}
        <div
          key={`content-${currentIndex}`}
          className={`onboarding-content animation-${direction}`}
        >
          <div
            className="slide-number"
            aria-hidden="true"
          >
            {String(currentIndex + 1).padStart(2, '0')}
          </div>

          <p className="slide-kicker">
            {String(currentIndex + 1).padStart(2, '0')} /{' '}
            {String(slides.length).padStart(2, '0')}
          </p>

          <h1>{title}</h1>

          <p className="onboarding-description">
            {text}
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="onboarding-footer">
        {/* INDICATEURS */}
        <div
          className="onboarding-progress"
          role="tablist"
          aria-label="Progression"
        >
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`${t(
                'onboarding.slide',
                {
                  defaultValue: 'Écran',
                }
              )} ${index + 1}`}
              className={
                index === currentIndex
                  ? 'active'
                  : ''
              }
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>

        {/* ACTIONS */}
        <div className="onboarding-actions">
          <button
            type="button"
            className="skip-button"
            onClick={goToLogin}
          >
            {t('onboarding.passer')}
          </button>

          <button
            type="button"
            className={`next-button ${
              isLastSlide
                ? 'start-button'
                : ''
            }`}
            onClick={handleNext}
          >
            <span>
              {isLastSlide
                ? t('onboarding.commencer')
                : t('onboarding.suivant')}
            </span>

            <span
              className="arrow"
              aria-hidden="true"
            >
              {isLastSlide ? '✓' : '→'}
            </span>
          </button>
        </div>
      </footer>
    </main>
  );
};

export default Onboarding;